import Cards from './Cards'
import { useEffect } from 'react'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { CubeLoader } from '@/components/ui/cube-loader'
import CarouselDialogs from './CarouselDialogs'
import { useCarouselCards } from './hooks/useCarouselCards'
import { useDeleteCard } from './hooks/useDeleteCard'
import { useThumbnailEditor } from './hooks/useThumbnailEditor'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useMutation } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { UPDATE_AD_ASSET_POSITIONS, GET_AD_ASSETS } from '@/graphql/ads/ad-assets-queries'

const CarouselCards = () => {
    const {
        id: adId,
        campaignId,
        goToStep,
        setStepContentData,
        registerNextGuard,
        deregisterNextGuard,
        dataForStepper,
    } = useWizard()

    // Custom hooks for state management
    const {
        cards,
        expandedCard,
        setExpandedCard,
        handleApplyToAllCards,
        isApplyingToAll,
        loading,
        handleInputChange,
        removeCardFromState,
        refetchAssets,
        setCards,
    } = useCarouselCards(campaignId, adId)

    const { showDeleteDialog, cardToDelete, isDeleting, handleDeleteCard, confirmDeleteCard, cancelDelete } =
        useDeleteCard(campaignId, adId, removeCardFromState)

    const {
        showThumbnailEditor,
        selectedAsset,
        isUpdatingThumbnail,
        currentSelection,
        uploadState,
        openThumbnailEditor,
        closeThumbnailEditor,
        handleSelectionChange,
        handleSaveThumbnail,
    } = useThumbnailEditor(refetchAssets)

    useEffect(() => {
        const guard = registerNextGuard(() => {
            if (cards.length === 0) {
                showNotification('Please upload at least one media', 'info')
                return false
            }

            return true
        })
        return () => deregisterNextGuard(guard)
    }, [cards, registerNextGuard, deregisterNextGuard])

    const [updatePositions] = useMutation(UPDATE_AD_ASSET_POSITIONS, {
        update: (cache, { data }) => {
            const updatedAssets = data.updateAdAssetPositions
            const existingData = cache.readQuery({
                query: GET_AD_ASSETS,
                variables: { campaignId, adId },
            })

            if (existingData) {
                cache.writeQuery({
                    query: GET_AD_ASSETS,
                    variables: { campaignId, adId },
                    data: {
                        AdAsset: {
                            adAssets: updatedAssets.map((asset) => ({
                                ...existingData.AdAsset.adAssets.find((a) => a.id === asset.id),
                                position: asset.position,
                            })),
                            __typename: 'AdAsset',
                        },
                    },
                })
            }
        },
    })

    // Event handlers
    const handleToggleExpanded = (cardId) => setExpandedCard(expandedCard === cardId ? null : cardId)
    const handleDeleteCardClick = (cardId) => handleDeleteCard(cardId, cards)

    const handleCardsReorder = async (newCards) => {
        // Optimistically update the local state
        setCards(newCards)
        await updatePositions({
            variables: {
                updates: newCards.map((card, index) => ({
                    id: card.id,
                    position: index,
                })),
            },
            optimisticResponse: {
                updateAdAssetPositions: newCards.map((card, index) => ({
                    id: card.id,
                    position: index,
                    __typename: 'AdAsset',
                })),
            },
        })
    }

    if (loading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="carouselCards" />
            <div className="tw:flex-1 tw:overflow-auto">
                <div className="tw:p-4">
                    <span className="tw:text-sm tw:font-semibold">Carousel Cards</span>
                    <p className="tw:mt-2 tw:text-sm">
                        {cards.length} of 10 cards added. Drag and drop to change order.
                    </p>
                </div>

                <div className="tw:flex tw:flex-col tw:border-t">
                    <Cards
                        cards={cards}
                        onCardsReorder={handleCardsReorder}
                        isExpanded={expandedCard}
                        onToggleExpanded={handleToggleExpanded}
                        handleInputChange={handleInputChange}
                        handleDeleteCard={handleDeleteCardClick}
                        goToStep={goToStep}
                        openThumbnailEditor={openThumbnailEditor}
                        selectedAsset={selectedAsset}
                        isUpdatingThumbnail={isUpdatingThumbnail}
                        setStepContentData={setStepContentData}
                        handleApplyToAllCards={handleApplyToAllCards}
                        isApplyingToAll={isApplyingToAll}
                    />

                    <div className="tw:p-4">
                        <Button variant="secondary" className="tw:w-40" onClick={() => goToStep('uploadAssets')}>
                            <Plus size={16} />
                            Add Cards
                        </Button>
                    </div>
                </div>

                <CarouselDialogs
                    showDeleteDialog={showDeleteDialog}
                    cardToDelete={cardToDelete}
                    isDeleting={isDeleting}
                    onCancelDelete={cancelDelete}
                    onConfirmDelete={confirmDeleteCard}
                    showThumbnailEditor={showThumbnailEditor}
                    selectedAsset={selectedAsset}
                    onCloseThumbnailEditor={closeThumbnailEditor}
                    onSelectionChange={handleSelectionChange}
                    onSaveThumbnail={handleSaveThumbnail}
                    currentSelection={currentSelection}
                    uploadState={uploadState}
                />
            </div>
        </div>
    )
}

export default CarouselCards
